import { motion } from 'motion/react'
import { Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const shimmer = 'animate-shimmer bg-gradient-to-r from-charcoal/50 via-slate/50 to-charcoal/50 bg-[length:200%_100%]'

function Bar({ width, height = 'h-3' }: { width: string; height?: string }) {
  return <div className={`${shimmer} rounded ${width} ${height}`} />
}

/**
 * Skeleton placeholder shown while `generate_briefing` is running.
 * Mirrors BriefingCard's silhouette so the swap-in feels seamless when
 * the real briefing arrives.
 */
export function BriefingSkeleton() {
  const { t } = useTranslation('chat')

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="glass-card rounded-2xl overflow-hidden border border-gold/20 w-full max-w-md"
      role="status"
      aria-label={t('tools.activity.generate_briefing', 'Generating your briefing')}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-gold/10 to-gold/5 border-b border-gold/10">
        <Sun className="w-5 h-5 text-gold/60 shrink-0" />
        <div className="space-y-2 flex-1">
          <Bar width="w-40" height="h-4" />
          <Bar width="w-24" height="h-2.5" />
        </div>
      </div>

      {/* Two skeleton sections */}
      {[0, 1].map(s => (
        <div key={s} className="px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-2 mb-3">
            <div className={`${shimmer} w-3.5 h-3.5 rounded`} />
            <Bar width="w-20" height="h-2.5" />
          </div>
          <div className="space-y-3">
            {[0, 1, 2].map(i => (
              <div key={i} className="space-y-1.5">
                <Bar width={i === 1 ? 'w-1/3' : 'w-1/4'} height="h-2.5" />
                <Bar width={i === 0 ? 'w-3/4' : i === 1 ? 'w-full' : 'w-2/3'} height="h-3.5" />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Footer label — keeps the user informed about what's happening */}
      <div className="px-5 py-3 bg-gold/5 flex items-center justify-center gap-2">
        <motion.div
          className="w-1.5 h-1.5 rounded-full bg-gold/70"
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <span className="text-pearl/60 text-xs">
          {t('tools.activity.generate_briefing', 'Generating your briefing')}
        </span>
      </div>
    </motion.div>
  )
}
